import { useEffect, useState } from 'react';
import SigningLinkDialog from './SigningLinkDialog.jsx';
import { Button } from '@/components/ui/button.jsx';
import { requestBor, getBorRequestMessages } from '@/services/deals.service.js';

// "Request BOR signature" on a Deal: the shared Tower signing-link dialog,
// pointed at /api/bor/deal/:id/request. The letter is filled in from the
// deal's application; the client only signs. The broker picks the message
// the email carries (David 2026-11-04): a preset fills the box, Custom empties
// it, and the box is what is sent. The choices, the limit and which preset to
// start on (with its reason) are the server's
// (GET /api/bor/deal/:id/request-messages); when the server could not check,
// nothing is picked and the broker picks. The door validates what is sent and
// records the choice.
export default function BorRequestDialog(props) {
  const { open, dealId } = props;
  const [choices, setChoices] = useState(null); // { options, max, why } or { error }
  const [picked, setPicked] = useState(null);
  const [text, setText] = useState('');

  useEffect(() => {
    if (!open || !dealId) return;
    setChoices(null);
    setPicked(null);
    setText('');
    getBorRequestMessages(dealId)
      .then((res) => {
        const presets = Array.isArray(res?.presets) ? res.presets : [];
        const options = res?.custom ? [...presets, { ...res.custom, text: '' }] : presets;
        const start = presets.find((p) => p.key === res?.preselect?.preset) || null;
        setChoices({ options, max: res?.max ?? null, why: res?.preselect?.why || null });
        if (start) { setPicked(start.key); setText(start.text); }
      })
      .catch(() => setChoices({ error: 'Could not load the message choices. Write the message yourself, or close and reopen to try again.' }));
  }, [open, dealId]);

  const options = choices?.options || [];
  const max = choices?.max ?? null;
  const pickedOption = options.find((p) => p.key === picked) || null;
  const length = text.trim().length;
  const over = max != null && length > max;
  const empty = length === 0;
  // Words typed with nothing picked are the broker's own: sent as Custom.
  const choice = picked || 'custom';

  let note = null;
  if (!choices) note = <span className="text-text-muted">Loading the message choices...</span>;
  else if (choices.error) note = <span className="text-warning">{choices.error}</span>;
  else if (empty && choices) {
    note = <span className="text-warning">{picked === 'custom' ? 'Write your message to send.' : picked ? 'Write a message to send.' : 'Pick a message above, or write one, to send.'}</span>;
  } else if (over) note = <span className="text-warning">{length - max === 1 ? '1 character over' : `${length - max} characters over`}; shorten it to send.</span>;
  else if (pickedOption && pickedOption.text && text.trim() !== pickedOption.text) note = <span className="text-text-muted">Edited from "{pickedOption.label}".</span>;

  const extra = (
    <div className="my-2 space-y-2" data-bor-message>
      <div className="text-text-muted text-xs uppercase tracking-wider">Message to the client</div>
      {options.length ? (
        <div role="radiogroup" aria-label="Message choices" className="flex flex-wrap gap-2">
          {options.map((p) => (
            <Button
              key={p.key}
              type="button"
              size="sm"
              role="radio"
              aria-checked={picked === p.key}
              data-preset={p.key}
              variant={picked === p.key ? 'secondary' : 'outline'}
              className="h-auto whitespace-normal text-left"
              onClick={() => { setPicked(p.key); setText(p.text); }}
            >
              {p.label}
            </Button>
          ))}
        </div>
      ) : null}
      {choices?.why ? <div className="text-xs text-text-muted" data-preselect-why>{choices.why}</div> : null}
      <textarea
        aria-label="Message to the client"
        rows={5}
        value={text}
        onChange={(e) => setText(e.target.value)}
        disabled={!choices}
        className="w-full rounded-card border border-border bg-bg px-3 py-2 text-sm text-text focus:border-accent focus:outline-none focus:ring-2 focus:ring-accent disabled:opacity-50"
      />
      <div className="flex items-start justify-between gap-3 text-xs">
        <span>{note}</span>
        <span data-count className={over ? 'shrink-0 text-warning' : 'shrink-0 text-text-muted'}>
          {max != null ? `${length} / ${max}` : `${length}`}
        </span>
      </div>
    </div>
  );

  return (
    <SigningLinkDialog
      {...props}
      request={(id) => requestBor(id, { message: text, choice })}
      docName="Broker of Record letter"
      shortName="BOR"
      about="The letter is already filled in from the application; the client only signs."
      extra={extra}
      sendDisabled={!choices || empty || over}
      draft={text}
    />
  );
}
